import type { PageBlockDefinition } from '../definition-types'

// ─── Layout Presets ───────────────────────────────────────────────────────────
// The preset name is the serialized value stored in PageBlockInstance.config.
// EDITORIAL_GRID_PRESET_CONFIG maps each preset to its display label, active
// column count, and CSS grid-template-columns value.

export type EditorialGridLayoutPreset =
  | '2-equal'
  | '2-wide-left'
  | '2-wide-right'
  | '2-feature-rail'
  | '2-rail-feature'
  | '3-equal'
  | '3-wide-left'
  | '3-wide-center'
  | '3-wide-right'
  | '3-feature-rail'
  | '3-rail-feature'

export interface EditorialGridPresetConfig {
  label: string
  columns: 2 | 3
  gridTemplate: string
}

export const EDITORIAL_GRID_PRESET_CONFIG: Record<
  EditorialGridLayoutPreset,
  EditorialGridPresetConfig
> = {
  '2-equal':        { label: 'Equal halves',         columns: 2, gridTemplate: '1fr 1fr' },
  '2-wide-left':    { label: 'Weighted left',         columns: 2, gridTemplate: '3fr 2fr' },
  '2-wide-right':   { label: 'Weighted right',        columns: 2, gridTemplate: '2fr 3fr' },
  '2-feature-rail': { label: 'Feature + rail',        columns: 2, gridTemplate: '4fr 1fr' },
  '2-rail-feature': { label: 'Rail + feature',        columns: 2, gridTemplate: '1fr 4fr' },
  '3-equal':        { label: 'Three equal',           columns: 3, gridTemplate: '1fr 1fr 1fr' },
  '3-wide-left':    { label: 'Wide left',             columns: 3, gridTemplate: '2fr 1fr 1fr' },
  '3-wide-center':  { label: 'Wide center',           columns: 3, gridTemplate: '1fr 2fr 1fr' },
  '3-wide-right':   { label: 'Wide right',            columns: 3, gridTemplate: '1fr 1fr 2fr' },
  '3-feature-rail': { label: 'Double feature + rail', columns: 3, gridTemplate: '2fr 2fr 1fr' },
  '3-rail-feature': { label: 'Rail + double feature', columns: 3, gridTemplate: '1fr 2fr 2fr' },
}

// ─── Definition ───────────────────────────────────────────────────────────────

export const editorialGridDefinition: PageBlockDefinition = {
  type: 'editorial-grid',
  label: 'Editorial Grid',
  description:
    'A configurable editorial grid with 2 or 3 columns of article blocks side by side. Column count and widths are controlled by the layout preset.',
  rowCompatible: true,
  columnCompatible: false,
  category: 'editorial',
  minArticles: 0,
  maxArticles: 0,
  slots: [
    { name: 'col-1', label: 'Column 1', required: true },
    { name: 'col-2', label: 'Column 2', required: true },
    { name: 'col-3', label: 'Column 3', required: false },
  ],
  defaultConfig: {
    layoutPreset: '3-equal',
    showDividers: true,
    column1: null,
    column2: null,
    column3: null,
  },
  editorFields: [
    {
      key: 'layoutPreset',
      label: 'Layout Preset',
      type: 'select',
      options: Object.entries(EDITORIAL_GRID_PRESET_CONFIG).map(([value, cfg]) => ({
        label: `${cfg.label} (${cfg.gridTemplate})`,
        value,
      })),
    },
    { key: 'showDividers', label: 'Show Column Dividers', type: 'toggle' },
  ],
}
